import { useDispatch } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_DETAILS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useLocation, useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { useClientEdit } from "../hooks/useClientEdit";
import {
  checkTrimForEdit,
  getOptionValue,
  getOptionValues,
  NEW_CLIENT,
  YES_NO_OPTIONS,
} from "../utils/clientsUtils";
import { EditClientPayload, NewClient } from "../types/clients-module-types";
import {
  SelectItem,
  SelectItemBoolean,
  SelectItemNumber,
} from "../../SharedModule/types/shared-module-types";
import { ClientsService } from "../services/clients";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { ClientFormFields } from "../components/ClientFormFields";
import {
  setIsClientDetailInfoLoading,
  setNeedClientInfoUpdate,
} from "../redux/reducers/clientDetails.reducer";
import {
  setNeedHeaderRefresh,
  setNeedRefresh,
} from "../redux/reducers/clientsList.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  // If there is no id, go to clients list
  if (!id) {
    navigate(ROUTE_CLIENTS);
  }

  const {
    ClientForHeaderInfo,
    isLoadingDropdownInfo,
    isAllUsersWithProfileLoading,
    dropdownsValues,
    allUsers,
    isClientDetailInfoLoading,
    clientDetails,
    userRolesList,
    rolesLoaded,
    isBillingInfoLoading,
    clientBillingInfo,
    isClientDetailInfoLoaded,
    isBillingInfoLoaded,
  } = useClientEdit(Number(id));

  useEffect(() => {
    document.title = "The Hub - Edit Client";

    return () => {
      resetScrollY(globalThis.location.pathname);
    };
  }, []);

  useEffect(() => {
    if (
      dropdownsValues &&
      clientDetails &&
      clientBillingInfo &&
      !isAllUsersWithProfileLoading &&
      isClientDetailInfoLoaded &&
      isBillingInfoLoaded
    ) {
      let tempInvoiceFormatId = 0,
        tempRelationshipTypeId = 0,
        tempIndustryTypeId = 0,
        tempSourceTypeId = 0,
        tempBillingCycleId = 0,
        tempBillingTermId = 0,
        tempTaxJurisdictionId = 0;

      setCompanyOwner(
        allUsers?.find(
          (user) => user.value === clientDetails.companyOwnerUserId
        )
      );
      setActualCountry(
        dropdownsValues?.countries.find(
          (country) => country.label === clientDetails.country
        )
      );
      setActualState(
        dropdownsValues?.states.find(
          (state) => state.label === clientDetails.state
        )
      );
      setInvoiceFormat(
        dropdownsValues?.invoiceFormats.find((format) => {
          if (format.label === clientBillingInfo.invoiceFormat) {
            tempInvoiceFormatId = format.value;
            return format;
          }
        })
      );
      setRelationshipType(
        dropdownsValues?.relationshipTypes.find((type) => {
          if (type.label === clientDetails.relationship) {
            tempRelationshipTypeId = type.value;
            return type;
          }
        })
      );
      setIndustryType(
        dropdownsValues?.industryTypes.find((type) => {
          if (type.label === clientDetails.industry) {
            tempIndustryTypeId = type.value;
            return type;
          }
        })
      );
      setSourceType(
        dropdownsValues?.sourceTypes.find((type) => {
          if (type.label === clientDetails.originalSource) {
            tempSourceTypeId = type.value;
            return type;
          }
        })
      );
      setBillingCycle(
        dropdownsValues?.billingCycles.find((cycle) => {
          if (cycle.label === clientBillingInfo.billingCycle) {
            tempBillingCycleId = cycle.value;
            return cycle;
          }
        })
      );
      setBillingTerm(
        dropdownsValues?.billingTerms.find((cycle) => {
          if (cycle.label === clientBillingInfo.terms) {
            tempBillingTermId = cycle.value;
            return cycle;
          }
        })
      );
      setTaxJurisdiction(
        dropdownsValues?.taxJurisdictions.find((tax) => {
          if (tax.label === clientBillingInfo.taxJurisdiction) {
            tempTaxJurisdictionId = tax.value;
            return tax;
          }
        })
      );
      setLeadGenerationSourceTypeId(
        dropdownsValues?.leadGenerationSourceTypes.find(
          (tax) => tax.value === clientDetails.leadGenerationSource
        )
      );
      setReferredBy(
        dropdownsValues?.referralTypes.find((type) =>
          clientDetails.referredBy
            ? type.label.toLowerCase() === clientDetails.referredBy.toLowerCase()
            : type.label.toLowerCase() === "internal"
        )
      );
      setCustomMasterAgreement(
        dropdownsValues?.customMasterAgreements.find(
          (tax) => tax.label === clientDetails.customMasterAgreement
        )
      );
      setCustomNda(
        dropdownsValues?.customNdas.find(
          (item) => item.label === clientDetails.customNda
        )
      );
      setComprehensiveCareClient(
        YES_NO_OPTIONS.find(
          (option) => option.value === clientDetails.comprehensiveCareClient
        ) ?? null
      );
      setContinuousInnovationClient(
        YES_NO_OPTIONS.find(
          (option) => option.value === clientDetails.continuousInnovationClient
        ) ?? null
      );
      setPurchaseOrderRequired(
        YES_NO_OPTIONS.find(
          (option) => option.value === clientDetails.purchaseOrderRequired
        ) ?? null
      );

      setUseAuthorization(
        YES_NO_OPTIONS.find(
          (option) => option.value === clientDetails.useAuthorization
        ) ?? null
      );

      setClientProfile({
        ...clientProfile,
        invoiceFormatId: tempInvoiceFormatId,
        relationshipTypeId: tempRelationshipTypeId,
        industryTypeId: tempIndustryTypeId,
        sourceTypeId: tempSourceTypeId,
        billingCycleId: tempBillingCycleId,
        billingTermId: tempBillingTermId,
        taxJurisdictionId: tempTaxJurisdictionId,
        leadGenerationSourceTypeIds: clientDetails.leadGenerationSourceTypeIds,
        companyOwnerUserId: clientDetails.companyOwnerUserId,
        companyName: ClientForHeaderInfo.companyName,
        companyAliasName: clientDetails.companyAliasName,
        streetAddress2: clientDetails.streetAddress2,
        phoneNumber: clientDetails.phoneNumber,
        websiteUrl: clientDetails.websiteUrl,
        companyDomainName: clientDetails.companyDomainName,
        legalNotes: clientDetails.legalNotes,
        legalInformationLocation: clientDetails.legalInformationLocation,
        comprehensiveCareClient: clientDetails.comprehensiveCareClient,
        continuousInnovationClient: clientDetails.continuousInnovationClient,
        customNdaId: clientDetails.customNdaId,
        purchaseOrderRequired: clientDetails.purchaseOrderRequired,
        referredBy: clientDetails.referredBy,
        useAuthorization: clientDetails.useAuthorization,
        accountingNotes: clientBillingInfo.accountingNotes,
      });
    }
  }, [
    dropdownsValues,
    clientDetails,
    clientBillingInfo,
    isAllUsersWithProfileLoading,
    isClientDetailInfoLoaded,
    isBillingInfoLoaded,
  ]);

  const [formTouched, setFormTouched] = useState(false);

  const [clientProfile, setClientProfile] = useState<NewClient>(NEW_CLIENT);
  const [actualCountry, setActualCountry] = useState<SelectItemNumber | null>(
    null
  );
  const [actualState, setActualState] = useState<SelectItemNumber | null>(null);
  const [companyOwner, setCompanyOwner] = useState<SelectItem | null>(null);
  const [invoiceFormat, setInvoiceFormat] = useState<SelectItemNumber | null>(
    null
  );
  const [relationshipType, setRelationshipType] =
    useState<SelectItemNumber | null>(null);
  const [industryType, setIndustryType] = useState<SelectItemNumber | null>(
    null
  );
  const [sourceType, setSourceType] = useState<SelectItemNumber | null>(null);
  const [billingCycle, setBillingCycle] = useState<SelectItemNumber | null>(
    null
  );
  const [billingTerm, setBillingTerm] = useState<SelectItemNumber | null>(null);
  const [taxJurisdiction, setTaxJurisdiction] =
    useState<SelectItemNumber | null>(null);
  const [leadGenerationSourceTypeId, setLeadGenerationSourceTypeId] = useState<
    SelectItemNumber[] | null
  >(null);
  const [referredBy, setReferredBy] = useState<SelectItemNumber | null>(null);
  const [customMasterAgreement, setCustomMasterAgreement] =
    useState<SelectItemNumber | null>(null);
  const [customNda, setCustomNda] = useState<SelectItemNumber | null>(null);

  const [currentClient] = useState<SelectItemBoolean>(
    YES_NO_OPTIONS.find(
      (option) => option.value === clientProfile.currentClient
    ) || YES_NO_OPTIONS[0]
  );
  const [comprehensiveCareClient, setComprehensiveCareClient] =
    useState<SelectItemBoolean | null>(
      YES_NO_OPTIONS.find(
        (option) => option.value === clientProfile.comprehensiveCareClient
      ) ?? null
    );
  const [continuousInnovationClient, setContinuousInnovationClient] =
    useState<SelectItemBoolean | null>(
      YES_NO_OPTIONS.find(
        (option) => option.value === clientProfile.continuousInnovationClient
      ) ?? null
    );
  const [purchaseOrderRequired, setPurchaseOrderRequired] =
    useState<SelectItemBoolean | null>(
      YES_NO_OPTIONS.find(
        (option) => option.value === clientProfile.purchaseOrderRequired
      ) ?? null
    );
  const [useAuthorization, setUseAuthorization] =
    useState<SelectItemBoolean | null>(
      YES_NO_OPTIONS.find(
        (option) => option.value === clientProfile.useAuthorization
      ) ?? null
    );
  const [taxable] = useState<SelectItemBoolean | null>(
    YES_NO_OPTIONS.find((option) => option.value === clientProfile.taxable) ??
      null
  );

  const [editButtonDisabled, setEditButtonDisabled] = useState(true);

  useEffect(() => {
    const isDisabled =
      clientProfile.companyName.length === 0 ||
      !companyOwner ||
      clientProfile.streetAddress.length === 0 ||
      clientProfile.city.length === 0 ||
      !actualState ||
      clientProfile.postalCode.length === 0 ||
      !actualCountry ||
      clientProfile.priceSchedule === 0 ||
      !relationshipType ||
      !industryType ||
      !sourceType ||
      !billingCycle ||
      !billingTerm ||
      !taxable ||
      !taxJurisdiction ||
      !invoiceFormat;

    setEditButtonDisabled(isDisabled);
  }, [
    clientProfile.companyName,
    companyOwner,
    clientProfile.streetAddress,
    clientProfile.city,
    actualState,
    clientProfile.postalCode,
    actualCountry,
    clientProfile.priceSchedule,
    relationshipType,
    industryType,
    sourceType,
    billingCycle,
    billingTerm,
    taxable,
    taxJurisdiction,
    invoiceFormat,
  ]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setFormTouched(true);

    setClientProfile((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        [name]: value,
      };
    });
  };

  const handleChangeDropdown = (name: string, elem: any) => {
    setFormTouched(true);
    // Add extra logic here for dropdowns to keep update profile state
    switch (name) {
      case "companyOwnerUserId":
        setCompanyOwner(elem);
        setClientProfile({
          ...clientProfile,
          companyOwnerUserId: elem.value,
        });
        break;
      case "comprehensiveCareClient":
        setComprehensiveCareClient(elem);
        setClientProfile({
          ...clientProfile,
          comprehensiveCareClient: elem.value,
        });
        break;
      case "continuousInnovationClient":
        setContinuousInnovationClient(elem);
        setClientProfile({
          ...clientProfile,
          continuousInnovationClient: elem.value,
        });
        break;
      case "purchaseOrderRequired":
        setPurchaseOrderRequired(elem);
        setClientProfile({
          ...clientProfile,
          purchaseOrderRequired: elem.value,
        });
        break;
      case "useAuthorization":
        setUseAuthorization(elem);
        setClientProfile({
          ...clientProfile,
          useAuthorization: elem.value,
        });
        break;
      case "customNda":
        setCustomNda(elem);
        setClientProfile({
          ...clientProfile,
          customNdaId: elem.value,
        });
        break;
      case "invoiceFormatId":
        setInvoiceFormat(elem);
        setClientProfile({
          ...clientProfile,
          invoiceFormatId: elem.value,
        });
        break;
      case "leadGenerationSourceTypeId":
        setLeadGenerationSourceTypeId(elem);
        setClientProfile({
          ...clientProfile,
          leadGenerationSourceTypeIds: elem,
        });
        break;
      case "referredTypeId":
        setReferredBy(elem);
        setClientProfile({
          ...clientProfile,
          referredBy: elem.value,
        });
        break;
      case "customMasterAgreementId":
        setCustomMasterAgreement(elem);
        setClientProfile({
          ...clientProfile,
          customMasterAgreement: elem.value,
        });
        break;
    }
  };

  const editClient = async () => {
    const body: EditClientPayload = {
      originalCompanyName: ClientForHeaderInfo.companyName,
      isActive: ClientForHeaderInfo.isActive,
      accountingNotes: checkTrimForEdit(clientProfile.accountingNotes),
      billingCycleId: getOptionValue(billingCycle),
      billingTermId: getOptionValue(billingTerm),
      city: clientProfile.city,
      companyAliasName: checkTrimForEdit(clientProfile.companyAliasName),
      companyDomainName: checkTrimForEdit(clientProfile.companyDomainName),
      companyName: clientProfile.companyName.trim(),
      companyOwnerUserId: getOptionValue(companyOwner),
      comprehensiveCareClient: getOptionValue(comprehensiveCareClient),
      continuousInnovationClient: getOptionValue(continuousInnovationClient),
      countryId: getOptionValue(actualCountry),
      currentClient: currentClient.value,
      customMasterAgreementId: getOptionValue(customMasterAgreement),
      customNdaId: getOptionValue(customNda),
      industryTypeId: getOptionValue(industryType),
      invoiceFormatId: getOptionValue(invoiceFormat),
      leadGenerationSourceTypeIds: getOptionValues(
        leadGenerationSourceTypeId
      ),
      legalInformationLocation: checkTrimForEdit(
        clientProfile.legalInformationLocation
      ),
      legalNotes: checkTrimForEdit(clientProfile.legalNotes),
      phoneNumber: checkTrimForEdit(clientProfile.phoneNumber),
      postalCode: clientProfile.postalCode.trim(),
      priceSchedule: clientProfile.priceSchedule,
      purchaseOrderRequired: getOptionValue(purchaseOrderRequired),
      referralTypeId: getOptionValue(referredBy),
      relationshipTypeId: getOptionValue(relationshipType),
      sourceTypeId: getOptionValue(sourceType),
      stateId: getOptionValue(actualState),
      streetAddress: clientProfile.streetAddress,
      streetAddress2: checkTrimForEdit(clientProfile.streetAddress2),
      taxJurisdictionId: getOptionValue(taxJurisdiction),
      taxable: getOptionValue(taxable),
      useAuthorization: getOptionValue(useAuthorization),
      websiteUrl: checkTrimForEdit(clientProfile.websiteUrl),
    };

    if (
      body.originalCompanyName !== body.companyName ||
      clientDetails.companyOwnerUserId !== companyOwner?.value
    ) {
      dispatch(setNeedHeaderRefresh(true));
    }

    await ClientsService.editInternalClient(body)
      .then((res) => {
        dispatch(
          addToast({
            mode: "success",
            message: res.message,
          })
        );
        dispatch(setNeedClientInfoUpdate(true));
        dispatch(setIsClientDetailInfoLoading(true));
        dispatch(setNeedRefresh(true));
        navigate(ROUTE_CLIENTS_DETAILS.replace(":id", String(id)));
      })
      .catch((err) => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something went wrong creating this internal client",
          })
        );
      });
  };

  return (
    <div className="content">
      <HeaderClientsModule
        isLoadingPage={
          isClientDetailInfoLoading ||
          isBillingInfoLoading ||
          !rolesLoaded ||
          isLoadingDropdownInfo ||
          isAllUsersWithProfileLoading
        }
        backFromRelationshipPage={
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
        }
      />

      {(isClientDetailInfoLoading ||
        isBillingInfoLoading ||
        !rolesLoaded ||
        isLoadingDropdownInfo ||
        isAllUsersWithProfileLoading) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isClientDetailInfoLoading &&
        !isBillingInfoLoading &&
        rolesLoaded &&
        !isLoadingDropdownInfo &&
        !isAllUsersWithProfileLoading &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isClientDetailInfoLoading &&
        !isBillingInfoLoading &&
        rolesLoaded &&
        !isLoadingDropdownInfo &&
        !isAllUsersWithProfileLoading &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <>
            <div className="content-header" style={{ margin: "25px 0" }}>
              <h2>Edit Client</h2>
            </div>

            <form className="card entry-hour-box-background px-4 py-2">
              <ClientFormFields
                allUsers={allUsers}
                isAllUsersWithProfileLoading={isAllUsersWithProfileLoading}
                dropdownsValues={dropdownsValues}
                clientProfile={clientProfile}
                handleChange={handleChange}
                handleChangeDropdown={handleChangeDropdown}
                currentClient={currentClient}
                companyOwner={companyOwner}
                actualState={actualState}
                actualCountry={actualCountry}
                relationshipType={relationshipType}
                industryType={industryType}
                sourceType={sourceType}
                referredBy={referredBy}
                leadGenerationSourceTypeId={leadGenerationSourceTypeId}
                comprehensiveCareClient={comprehensiveCareClient}
                continuousInnovationClient={continuousInnovationClient}
                purchaseOrderRequired={purchaseOrderRequired}
                useAuthorization={useAuthorization}
                customMasterAgreement={customMasterAgreement}
                customNda={customNda}
                billingCycle={billingCycle}
                invoiceFormat={invoiceFormat}
                billingTerm={billingTerm}
                taxable={taxable}
                taxJurisdiction={taxJurisdiction}
                priceScheduleInputStyle={{ border: "1px solid #8b90aa" }}
                submitButton={
                  <div className="d-flex p-0 my-3">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => editClient()}
                      disabled={editButtonDisabled || !formTouched}
                    >
                      Save Changes
                    </button>
                  </div>
                }
              />
            </form>
          </>
        )}
      <Footer />
    </div>
  );
};
